// @ts-strict-ignore
/**
 * Contains all code related to Editor Access state and logic.
 * We provide a simple API to verify if a workflow is read-only, or if a user can perform
 * a certain action based on permissions (todo BUILD-2772)
 */
import type { PropsWithChildren } from 'react';
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { UserContext } from '~/dashboard/context';
import { useGetWorkflowFederationStatusQuery } from '~/helpers/api-queries/embeddedFinance';
import { useAppSelector } from '~/store/reduxHooks';
import { selectIsWorkflowLockOwnedByAgent, selectWorkflow } from 'flow/store/redux/selectors';
import { EditorMode } from 'flow/editor/context/constants';

type EditorAccess = {
  isLoading: boolean;
  editorMode: EditorMode;
  // todo BUILD-2772 - move all permissions checks here
  // permissions: { }
};

type EditorAccessContextType = EditorAccess & {
  setEditorMode: (mode: EditorMode) => void;
  resetEditorMode: () => void;
};

const defaultEditorAccess: EditorAccess = {
  isLoading: false,
  editorMode: EditorMode.Standard,
};

const EditorAccessContext = createContext<EditorAccessContextType>({
  ...defaultEditorAccess,
  setEditorMode: () => { },
  resetEditorMode: () => { },
});

/**
 * Determines the initial editor mode based on feature flags
 */
const getInitialEditorMode = (
  hasWorkflowLocking: boolean,
  isWorkflowLockOwnedByAgent: boolean,
  hasCreateApplicationVersionPermission: boolean,
): EditorMode => {
  if (!hasCreateApplicationVersionPermission) {
    return EditorMode.ReadOnly;
  }

  if (hasWorkflowLocking) {
    return isWorkflowLockOwnedByAgent ? EditorMode.CheckedOut : EditorMode.Checkout;
  }

  return EditorMode.Standard;
};

export function EditorAccessProvider({ children }: PropsWithChildren<unknown>) {
  const {
    customer: {
      isAfefChildOrganization,
    },
    permissions,
    featureFlags,
  } = useContext(UserContext);

  const { application_token: workflowToken } = useAppSelector(selectWorkflow) ?? {};
  const isWorkflowLockOwnedByAgent = useAppSelector(selectIsWorkflowLockOwnedByAgent);

  const hasWorkflowLocking = featureFlags?.workflowLocking;
  const hasCreateApplicationVersionPermission = Boolean(permissions?.['CREATE:ApplicationVersions:null']);
  const federationMetadataQueryEnabled = Boolean(workflowToken) && isAfefChildOrganization;

  const [initialEditorMode, setInitialEditorMode] = useState<EditorMode>(
    getInitialEditorMode(
      hasWorkflowLocking,
      isWorkflowLockOwnedByAgent,
      hasCreateApplicationVersionPermission,
    ),
  );

  const [editorAccess, setEditorAccess] = useState<EditorAccess>({
    isLoading: federationMetadataQueryEnabled,
    editorMode: initialEditorMode,
  });

  const { data: workflowFederationMetadata } = useGetWorkflowFederationStatusQuery(
    { workflowToken },
    {
      enabled: federationMetadataQueryEnabled,
      retry: false,
      onSuccess: workflowFederationMetadataResult => {
        // wfs are federated when they are owned by a parent and are opened from a child account
        const isFederatedWorkflow = workflowFederationMetadataResult.is_federated;

        // ensure we force into readonly mode if federated
        if (isFederatedWorkflow) {
          if (initialEditorMode !== EditorMode.ReadOnly) {
            setInitialEditorMode(EditorMode.ReadOnly);
          }

          setEditorAccess({
            editorMode: EditorMode.ReadOnly,
            isLoading: false,
          });

          return;
        }

        // otherwise, mark as done loading
        setEditorAccess(prev => ({
          ...prev,
          isLoading: false,
        }));
      },
    },
  );

  const setEditorMode = useCallback((mode: EditorMode) => {
    setEditorAccess(prev => ({
      ...prev,
      editorMode: mode,
    }));
  }, []);

  /**
   * Restore editor mode from current permissions and workflow lock state — not the mode from
   * first mount. Otherwise after checkout (if using workflow locking) + temporary ReadOnly (e.g. paste),
   * reset would incorrectly return to Checkout even though Redux reflects the current lock owner.
   */
  const resetEditorMode = useCallback(() => {
    const isFederatedWorkflow = Boolean(
      federationMetadataQueryEnabled && workflowFederationMetadata?.is_federated,
    );
    const nextMode = isFederatedWorkflow
      ? EditorMode.ReadOnly
      : getInitialEditorMode(
        Boolean(hasWorkflowLocking),
        isWorkflowLockOwnedByAgent,
        hasCreateApplicationVersionPermission,
      );
    setEditorAccess(prev => ({
      ...prev,
      editorMode: nextMode,
    }));
  }, [
    federationMetadataQueryEnabled,
    workflowFederationMetadata?.is_federated,
    hasWorkflowLocking,
    isWorkflowLockOwnedByAgent,
    hasCreateApplicationVersionPermission,
  ]);

  const editorAccessValue = useMemo(() => ({
    ...editorAccess,
    setEditorMode,
    resetEditorMode,
  }), [editorAccess, setEditorMode, resetEditorMode]);

  return (
    <EditorAccessContext.Provider value={editorAccessValue}>
      {children}
    </EditorAccessContext.Provider>
  );
}

export function useEditorAccess(): EditorAccessContextType {
  // will return the default value if no context provider is present
  return useContext(EditorAccessContext);
}
